import React, { useEffect, useRef, useState } from "react";

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;

const CameraReveal = () => {
  const sectionRef = useRef(null);
  const [p, setP] = useState(0);
  const [mouse, setMouse] = useState({ x: 0, y: 0 }); // normalized -1..1

  const onMouseMove = (e) => {
    const nx = (e.clientX / window.innerWidth) * 2 - 1;
    const ny = (e.clientY / window.innerHeight) * 2 - 1;
    setMouse({ x: nx, y: ny });
  };
  const onMouseLeave = () => setMouse({ x: 0, y: 0 });

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = sectionRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const scrolled = Math.min(Math.max(-rect.top, 0), total);
        setP(total > 0 ? scrolled / total : 0);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  // two-image crossfade "reel" (A -> B)
  const t1 = 0.46, w = 0.14;
  const aOpacity = clamp(1 - (p - t1) / w);
  const bOpacity = clamp((p - t1) / w);
  const scaleA = 1.06 + p * 0.8;
  const scaleB = 1.08 + p * 0.4;

  // rule-of-thirds grid peaks mid-scroll
  const grid = clamp(1 - Math.abs(p - 0.5) / 0.33);

  const focal = Math.round(lerp(24, 85, p));
  const shutter = ["1/60", "1/125", "1/250", "1/500"][Math.min(3, Math.floor(p * 4))];
  const aperture = ["1.4", "2.0", "2.8", "4.0"][Math.min(3, Math.floor(p * 4))];
  const frames = Math.floor(p * 24 * 6) % 24;
  const secs = Math.floor(p * 6);
  const tc = `00:00:${String(secs).padStart(2, "0")}:${String(frames).padStart(2, "0")}`;

  const introFade = clamp(1 - p / 0.26);
  const endFade = clamp((p - 0.62) / 0.18);

  return (
    <section ref={sectionRef} className="relative bg-black" style={{ height: "260vh" }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden" onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        {/* Image A (futuristic Apple-Maps style DC — the shock opener) */}
        <img
          src="/renders/dc-futuristic-a.jpg"
          alt="Futuristic 3D map of Washington DC"
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
          style={{ transform: `translate3d(${mouse.x * -18}px, ${mouse.y * -12}px, 0) scale(${scaleA})`, transformOrigin: "50% 55%", opacity: aOpacity, transition: "transform 0.25s ease-out" }}
        />
        {/* Camera capture marker — blinking red "live tracking" reticle (tracks Image A) */}
        <div
          className="absolute inset-0 pointer-events-none z-[15] will-change-transform"
          style={{ transform: `translate3d(${mouse.x * -18}px, ${mouse.y * -12}px, 0) scale(${scaleA})`, transformOrigin: "50% 55%", opacity: aOpacity, transition: "transform 0.25s ease-out" }}
        >
          <div className="absolute" style={{ left: "16%", top: "30%" }}>
            <div className="relative flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-600" style={{ animation: "camBlink 1s steps(1) infinite", boxShadow: "0 0 10px 2px rgba(239,68,68,0.8)" }} />
              <span className="text-red-500 text-[9px] tracking-luxe font-sans-luxe" style={{ animation: "camBlink 1s steps(1) infinite" }}>REC ●</span>
              <span className="absolute -left-2 -top-2 w-7 h-7 border border-red-500/70" style={{ animation: "camPulse 1.6s ease-out infinite" }} />
            </div>
          </div>
          <style>{`@keyframes camBlink{0%,49%{opacity:1}50%,100%{opacity:0.15}}@keyframes camPulse{0%{transform:scale(1);opacity:.9}100%{transform:scale(1.9);opacity:0}}`}</style>
        </div>
        {/* Image B (luxury DC condo view — aspirational payoff) */}
        <img
          src="/renders/dc-condo-b.jpg"
          alt="Luxury Washington DC condo with skyline view"
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
          style={{ transform: `translate3d(${mouse.x * -22}px, ${mouse.y * -14}px, 0) scale(${scaleB})`, transformOrigin: "52% 48%", opacity: bOpacity, transition: "transform 0.25s ease-out" }}
        />

        {/* color grade + vignette */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 120% at 50% 45%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 100%)" }} />
        <div className="absolute inset-0 mix-blend-soft-light" style={{ background: "linear-gradient(180deg, rgba(90,150,210,0.16), rgba(180,147,106,0.12))" }} />

        {/* film grain */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.10] cc-grain" />

        {/* rule-of-thirds grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: grid * 0.5 }}>
          <div className="absolute top-1/3 left-0 w-full h-px bg-white/50" />
          <div className="absolute top-2/3 left-0 w-full h-px bg-white/50" />
          <div className="absolute left-1/3 top-0 h-full w-px bg-white/50" />
          <div className="absolute left-2/3 top-0 h-full w-px bg-white/50" />
        </div>

        {/* corner brackets */}
        {["tl","tr","bl","br"].map((c) => (
          <div key={c} className={`absolute w-8 h-8 md:w-12 md:h-12 border-white/80 z-20 ${
            c==="tl"?"top-6 left-6 md:top-10 md:left-10 border-t-2 border-l-2":
            c==="tr"?"top-6 right-6 md:top-10 md:right-10 border-t-2 border-r-2":
            c==="bl"?"bottom-6 left-6 md:bottom-10 md:left-10 border-b-2 border-l-2":
            "bottom-6 right-6 md:bottom-10 md:right-10 border-b-2 border-r-2"}`} />
        ))}

        {/* intro copy */}
        <div className="absolute inset-x-0 top-[18%] text-center px-6 z-20 transition-opacity duration-150" style={{ opacity: introFade }}>
          <p className="text-[11px] tracking-luxe uppercase text-white/85 mb-3">Delivering the region's most compelling</p>
          <h2 className="font-serif-display text-5xl md:text-7xl font-light text-white">Advertising</h2>
        </div>

        {/* center focus reticle (tracks the cursor) */}
        <div
          className="absolute left-1/2 top-1/2 z-20 pointer-events-none"
          style={{ opacity: clamp(1 - endFade), transform: `translate(-50%, -50%) translate3d(${mouse.x * 60}px, ${mouse.y * 45}px, 0)`, transition: "transform 0.18s ease-out" }}
        >
          <div className="relative w-24 h-24 md:w-36 md:h-36">
            <div className="absolute inset-0 border border-white/60" />
            <div className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-3 bg-white/70" />
            <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-px h-3 bg-white/70" />
            <div className="absolute top-1/2 left-0 -translate-y-1/2 h-px w-3 bg-white/70" />
            <div className="absolute top-1/2 right-0 -translate-y-1/2 h-px w-3 bg-white/70" />
            <span className="absolute -top-6 left-0 text-[#e7c9a3] text-[9px] tracking-luxe">{p > 0.5 ? "AF LOCK" : "AF"}</span>
          </div>
        </div>

        {/* top HUD */}
        <div className="absolute top-7 md:top-11 left-1/2 -translate-x-1/2 z-20 flex items-center gap-6 text-white/90 font-sans-luxe text-[10px] md:text-[11px] tracking-luxe">
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> REC</span>
          <span>4K</span>
          <span className="text-[#e7c9a3]">{tc}</span>
        </div>

        {/* bottom HUD */}
        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-20 w-[calc(100%-56px)] md:w-[calc(100%-96px)] max-w-[1200px] flex items-end justify-between text-white/90 font-sans-luxe text-[10px] md:text-[11px] tracking-luxe">
          <div className="flex flex-col gap-1">
            <span className="hidden sm:block">CAPITAL CORNER STUDIOS</span>
            <span>F {aperture} &nbsp; {shutter} &nbsp; ISO 100</span>
          </div>
          <div className="text-right flex flex-col gap-1 items-end">
            <span className="text-[#e7c9a3]">{focal}mm</span>
            <div className="w-24 md:w-44 h-[3px] bg-white/25 relative">
              <div className="absolute left-0 top-0 h-full bg-[#b4936a]" style={{ width: `${p * 100}%` }} />
            </div>
          </div>
        </div>

        {/* closing caption */}
        <div className="absolute inset-x-0 bottom-[24%] text-center px-6 z-20 transition-opacity duration-150" style={{ opacity: endFade }}>
          <p className="font-serif-display text-3xl md:text-5xl text-white font-light max-w-3xl mx-auto leading-tight">
            Your first showing <span className="italic text-[#e7c9a3]">begins online.</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default CameraReveal;
